import { apiSlice } from '../../../store/apiSlice';
import {
  BookIngestionDetail,
  BookIngestionSummary,
  BookDeleteImpact,
  BookPlanTopic,
  ExpedienteIngestionSummary,
  ExpedienteReportData,
  ExpedienteDocumentSummary,
  ExpedienteDocAnalysisHit,
  TimelineEvent,
  EntitySearchHit,
  Bot,
  BrowseHierarchyChildrenParams,
  BrowseSubtreeData,
  CreateBotInput,
  DocxTemplate,
  DomainListFilters,
  FolderTreeNode,
  IngestPolicyInput,
  KnowledgeDomain,
  LintReport,
  ListExpedienteIndexesData,
  ListExpedienteIndexesParams,
  LocateDataData,
  LocateHierarchyDataParams,
  NoragStats,
  DomainSource,
  UpdateBotInput,
  DomainDeleteImpact,
  CopilotConversationSummary,
  CopilotConversationDetail,
  CopilotMessage,
} from '../types';

export type { FolderTreeNode, BookPlanTopic };

export const noragApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    listDomains: builder.query<KnowledgeDomain[], DomainListFilters | void>({
      query: filters => ({
        url: '/norag/domains',
        params: filters || {},
      }),
      transformResponse: (response: { data: KnowledgeDomain[] }) => response.data,
      providesTags: result =>
        result
          ? [
              ...result.map(d => ({ type: 'NoragDomain' as const, id: d.slug })),
              { type: 'NoragDomain', id: 'LIST' },
            ]
          : [{ type: 'NoragDomain', id: 'LIST' }],
    }),
    getDomain: builder.query<KnowledgeDomain, string>({
      query: slug => `/norag/domains/${slug}`,
      transformResponse: (response: { data: KnowledgeDomain }) => response.data,
      providesTags: (_r, _e, slug) => [{ type: 'NoragDomain', id: slug }],
    }),
    getDomainContent: builder.query<string, string>({
      query: slug => `/norag/domains/${slug}/content`,
      transformResponse: (response: { data: { content: string } }) => response.data.content,
    }),
    ingestFile: builder.mutation<unknown, FormData>({
      query: body => ({
        url: '/norag/domains/ingest-file',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NoragDomain', id: 'LIST' },
        { type: 'NoragJob', id: 'LIST' },
        { type: 'NoragStats', id: 'STATS' },
      ],
    }),
    ingestUrl: builder.mutation<
      unknown,
      {
        slug: string;
        domainType: string;
        url: string;
        name?: string;
        context?: string;
        folderId?: string | null;
        policy?: IngestPolicyInput;
      }
    >({
      query: body => ({
        url: '/norag/domains/ingest-url',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NoragDomain', id: 'LIST' },
        { type: 'NoragJob', id: 'LIST' },
        { type: 'NoragStats', id: 'STATS' },
      ],
    }),
    deprecateDomain: builder.mutation<KnowledgeDomain, string>({
      query: slug => ({
        url: `/norag/domains/${slug}/deprecate`,
        method: 'POST',
      }),
      transformResponse: (response: { data: KnowledgeDomain }) => response.data,
      invalidatesTags: (_r, _e, slug) => [
        { type: 'NoragDomain', id: slug },
        { type: 'NoragDomain', id: 'LIST' },
        { type: 'NoragStats', id: 'STATS' },
      ],
    }),
    reactivateDomain: builder.mutation<KnowledgeDomain, string>({
      query: slug => ({
        url: `/norag/domains/${slug}/reactivate`,
        method: 'POST',
      }),
      transformResponse: (response: { data: KnowledgeDomain }) => response.data,
      invalidatesTags: (_r, _e, slug) => [
        { type: 'NoragDomain', id: slug },
        { type: 'NoragDomain', id: 'LIST' },
        { type: 'NoragStats', id: 'STATS' },
      ],
    }),
    getDomainDeleteImpact: builder.query<DomainDeleteImpact, string>({
      query: slug => `/norag/domains/${slug}/delete-impact`,
      transformResponse: (response: { data: DomainDeleteImpact }) => response.data,
    }),
    deleteDomainPermanent: builder.mutation<{ deleted: boolean }, string>({
      query: slug => ({
        url: `/norag/domains/${slug}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { data: { deleted: boolean } }) => response.data,
      invalidatesTags: (_r, _e, slug) => [
        { type: 'NoragDomain', id: slug },
        { type: 'NoragDomain', id: 'LIST' },
        { type: 'NoragStats', id: 'STATS' },
      ],
    }),
    listTemplates: builder.query<DocxTemplate[], void>({
      query: () => '/norag/templates',
      transformResponse: (response: { data: DocxTemplate[] }) => response.data,
      providesTags: [{ type: 'NoragTemplate', id: 'LIST' }],
    }),
    uploadTemplate: builder.mutation<unknown, FormData>({
      query: body => ({
        url: '/norag/templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'NoragTemplate', id: 'LIST' }],
    }),
    getNoragStats: builder.query<NoragStats, void>({
      query: () => '/norag/stats',
      transformResponse: (response: { data: NoragStats }) => response.data,
      providesTags: [{ type: 'NoragStats', id: 'STATS' }],
    }),
    runNoragLint: builder.mutation<LintReport, void>({
      query: () => ({
        url: '/norag/lint',
        method: 'POST',
      }),
      transformResponse: (response: { data: LintReport }) => response.data,
    }),
    listJobs: builder.query<DomainSource[], void>({
      query: () => '/norag/jobs',
      transformResponse: (response: { data: DomainSource[] }) => response.data,
      providesTags: [{ type: 'NoragJob', id: 'LIST' }],
    }),
    listFolders: builder.query<FolderTreeNode[], void>({
      query: () => '/norag/folders',
      transformResponse: (response: { data: FolderTreeNode[] }) => response.data,
      providesTags: [{ type: 'NoragFolder', id: 'TREE' }],
    }),
    createFolder: builder.mutation<FolderTreeNode, { name: string; parentId?: string | null }>({
      query: body => ({
        url: '/norag/folders',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: FolderTreeNode }) => response.data,
      invalidatesTags: [{ type: 'NoragFolder', id: 'TREE' }],
    }),
    rebuildFolderIndex: builder.mutation<{ ok: boolean }, string>({
      query: id => ({
        url: `/norag/folders/${id}/rebuild-index`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: { data: { ok: boolean } }) => response.data,
      invalidatesTags: [
        { type: 'NoragFolder', id: 'TREE' },
        { type: 'NoragHierarchy', id: 'EXPEDIENTES' },
        { type: 'NoragDomain', id: 'LIST' },
      ],
    }),
    listBooks: builder.query<BookIngestionSummary[], void>({
      query: () => '/norag/books',
      transformResponse: (response: { data: BookIngestionSummary[] }) => response.data,
      providesTags: [{ type: 'NoragBook', id: 'LIST' }],
    }),
    getBook: builder.query<BookIngestionDetail, string>({
      query: id => `/norag/books/${id}`,
      transformResponse: (response: { data: BookIngestionDetail }) => response.data,
      providesTags: (_r, _e, id) => [{ type: 'NoragBook', id }],
    }),
    ingestBook: builder.mutation<{ bookIngestionId: string }, FormData>({
      query: body => ({
        url: '/norag/books/ingest',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: { bookIngestionId: string } }) => response.data,
      invalidatesTags: [{ type: 'NoragBook', id: 'LIST' }],
    }),
    patchBookPlan: builder.mutation<unknown, { id: string; plan: BookPlanTopic[] }>({
      query: ({ id, plan }) => ({
        url: `/norag/books/${id}/plan`,
        method: 'PATCH',
        body: { plan },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'NoragBook', id: 'LIST' },
        { type: 'NoragBook', id },
        { type: 'NoragDomain', id: 'LIST' },
        { type: 'NoragJob', id: 'LIST' },
      ],
    }),
    confirmBook: builder.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: `/norag/books/${id}/confirm`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'NoragBook', id: 'LIST' },
        { type: 'NoragBook', id },
        { type: 'NoragDomain', id: 'LIST' },
        { type: 'NoragJob', id: 'LIST' },
      ],
    }),
    getBookDeleteImpact: builder.query<BookDeleteImpact, string>({
      query: id => `/norag/books/${id}/delete-impact`,
      transformResponse: (response: { data: BookDeleteImpact }) => response.data,
    }),
    deleteBook: builder.mutation<{ deleted: boolean }, string>({
      query: id => ({
        url: `/norag/books/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { data: { deleted: boolean } }) => response.data,
      invalidatesTags: (_r, _e, id) => [
        { type: 'NoragBook', id: 'LIST' },
        { type: 'NoragBook', id },
        { type: 'NoragDomain', id: 'LIST' },
        { type: 'NoragJob', id: 'LIST' },
      ],
    }),
    listBots: builder.query<Bot[], void>({
      query: () => '/norag/bots',
      transformResponse: (response: { data: Bot[] }) => response.data,
      providesTags: [{ type: 'NoragBot', id: 'LIST' }],
    }),
    getBot: builder.query<Bot, string>({
      query: id => `/norag/bots/${id}`,
      transformResponse: (response: { data: Bot }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'NoragBot', id }],
    }),
    createBot: builder.mutation<{ bot: Bot; token: string }, CreateBotInput>({
      query: body => ({
        url: '/norag/bots',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: { bot: Bot; token: string } }) => response.data,
      invalidatesTags: [{ type: 'NoragBot', id: 'LIST' }],
    }),
    updateBot: builder.mutation<Bot, { id: string; body: UpdateBotInput }>({
      query: ({ id, body }) => ({
        url: `/norag/bots/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: { data: Bot }) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'NoragBot', id },
        { type: 'NoragBot', id: 'LIST' },
      ],
    }),
    deactivateBot: builder.mutation<Bot, string>({
      query: id => ({
        url: `/norag/bots/${id}/deactivate`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: { data: Bot }) => response.data,
      invalidatesTags: (_result, _error, id) => [
        { type: 'NoragBot', id },
        { type: 'NoragBot', id: 'LIST' },
      ],
    }),
    rotateBotToken: builder.mutation<{ token: string }, string>({
      query: id => ({
        url: `/norag/bots/${id}/rotate-token`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: { data: { token: string } }) => response.data,
      invalidatesTags: (_result, _error, id) => [{ type: 'NoragBot', id }],
    }),
    exportOkfBundle: builder.mutation<Blob, void>({
      query: () => ({
        url: '/norag/export/okf',
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),
    listExpedienteIndexes: builder.query<ListExpedienteIndexesData, ListExpedienteIndexesParams | void>({
      query: params => ({
        url: '/norag/hierarchy/expedientes',
        params: params ?? {},
      }),
      transformResponse: (response: { data: ListExpedienteIndexesData }) => response.data,
      providesTags: [{ type: 'NoragHierarchy', id: 'EXPEDIENTES' }],
    }),
    browseHierarchyChildren: builder.query<BrowseSubtreeData, BrowseHierarchyChildrenParams>({
      query: ({ parentId, q, skip, take }) => ({
        url: '/norag/hierarchy/children',
        params: {
          ...(parentId != null ? { parentId } : {}),
          ...(q ? { q } : {}),
          ...(skip != null ? { skip } : {}),
          ...(take != null ? { take } : {}),
        },
      }),
      transformResponse: (response: { data: BrowseSubtreeData }) => response.data,
      providesTags: (_result, _error, { parentId }) => [
        { type: 'NoragHierarchy', id: parentId ?? 'ROOT' },
      ],
    }),
    locateHierarchyData: builder.query<LocateDataData, LocateHierarchyDataParams>({
      query: ({ q, pathPrefix, topK }) => ({
        url: '/norag/hierarchy/locate',
        params: {
          q,
          ...(pathPrefix ? { pathPrefix } : {}),
          ...(topK != null ? { topK } : {}),
        },
      }),
      transformResponse: (response: { data: LocateDataData }) => response.data,
    }),
    listExpedienteIngestions: builder.query<ExpedienteIngestionSummary[], void>({
      query: () => '/norag/expedientes',
      transformResponse: (response: { data: ExpedienteIngestionSummary[] }) => response.data,
      providesTags: [{ type: 'NoragExpediente', id: 'LIST' }],
    }),
    getExpedienteIngestion: builder.query<ExpedienteIngestionSummary, string>({
      query: id => `/norag/expedientes/${id}`,
      transformResponse: (response: { data: ExpedienteIngestionSummary }) => response.data,
      providesTags: (_r, _e, id) => [{ type: 'NoragExpediente', id }],
    }),
    ingestExpediente: builder.mutation<
      { expedienteId: string; filesEnqueued: number; skipped: string[] },
      FormData
    >({
      query: body => ({
        url: '/norag/expedientes/ingest',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: { expedienteId: string; filesEnqueued: number; skipped: string[] } }) =>
        response.data,
      invalidatesTags: [{ type: 'NoragExpediente', id: 'LIST' }, { type: 'NoragJob', id: 'LIST' }],
    }),
    getExpedienteReport: builder.query<ExpedienteReportData, string>({
      query: id => `/norag/expedientes/${id}/report`,
      transformResponse: (response: { data: ExpedienteReportData }) => response.data,
      providesTags: (_r, _e, id) => [{ type: 'NoragExpediente', id: `report-${id}` }],
    }),
    downloadExpedienteReportPdf: builder.mutation<Blob, string>({
      query: id => ({
        url: `/norag/expedientes/${id}/report/pdf`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),
    listExpedienteDocuments: builder.query<ExpedienteDocumentSummary[], string>({
      query: id => `/norag/expedientes/${id}/documents`,
      transformResponse: (response: { data: ExpedienteDocumentSummary[] }) => response.data,
      providesTags: (_r, _e, id) => [{ type: 'NoragExpediente', id: `docs-${id}` }],
    }),
    listExpedienteDocAnalyses: builder.query<ExpedienteDocAnalysisHit[], string>({
      query: id => `/norag/expedientes/${id}/doc-analyses`,
      transformResponse: (response: { data: ExpedienteDocAnalysisHit[] }) => response.data,
      providesTags: (_r, _e, id) => [{ type: 'NoragExpediente', id: `doc-analyses-${id}` }],
    }),
    addExpedienteDocuments: builder.mutation<
      { filesEnqueued: number; skipped: string[] },
      { id: string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/norag/expedientes/${id}/documents`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: { data: { filesEnqueued: number; skipped: string[] } }) =>
        response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'NoragExpediente', id },
        { type: 'NoragExpediente', id: 'LIST' },
        { type: 'NoragExpediente', id: `docs-${id}` },
        { type: 'NoragExpediente', id: `doc-analyses-${id}` },
        { type: 'NoragExpediente', id: `report-${id}` },
        { type: 'NoragJob', id: 'LIST' },
      ],
    }),
    replaceExpedienteDocument: builder.mutation<
      { sourceId: string; domainSlug: string },
      { expedienteId: string; sourceId: string; formData: FormData }
    >({
      query: ({ expedienteId, sourceId, formData }) => ({
        url: `/norag/expedientes/${expedienteId}/documents/${sourceId}`,
        method: 'PUT',
        body: formData,
      }),
      transformResponse: (response: { data: { sourceId: string; domainSlug: string } }) =>
        response.data,
      invalidatesTags: (_r, _e, { expedienteId }) => [
        { type: 'NoragExpediente', id: expedienteId },
        { type: 'NoragExpediente', id: `docs-${expedienteId}` },
        { type: 'NoragExpediente', id: `doc-analyses-${expedienteId}` },
        { type: 'NoragExpediente', id: `report-${expedienteId}` },
        { type: 'NoragJob', id: 'LIST' },
      ],
    }),
    deleteExpedienteDocument: builder.mutation<
      { deleted: boolean },
      { expedienteId: string; sourceId: string }
    >({
      query: ({ expedienteId, sourceId }) => ({
        url: `/norag/expedientes/${expedienteId}/documents/${sourceId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { data: { deleted: boolean } }) => response.data,
      invalidatesTags: (_r, _e, { expedienteId }) => [
        { type: 'NoragExpediente', id: expedienteId },
        { type: 'NoragExpediente', id: `docs-${expedienteId}` },
        { type: 'NoragExpediente', id: `doc-analyses-${expedienteId}` },
        { type: 'NoragExpediente', id: `report-${expedienteId}` },
        { type: 'NoragJob', id: 'LIST' },
      ],
    }),
    regenerateExpedienteReport: builder.mutation<{ queued: boolean }, string>({
      query: id => ({
        url: `/norag/expedientes/${id}/report/regenerate`,
        method: 'POST',
      }),
      transformResponse: (response: { data: { queued: boolean } }) => response.data,
      invalidatesTags: (_r, _e, id) => [
        { type: 'NoragExpediente', id },
        { type: 'NoragExpediente', id: `report-${id}` },
        { type: 'NoragExpediente', id: `doc-analyses-${id}` },
      ],
    }),
    getExpedienteTimeline: builder.query<TimelineEvent[], string>({
      query: id => `/norag/expedientes/${id}/timeline`,
      transformResponse: (response: { data: TimelineEvent[] }) => response.data,
    }),
    searchExpedienteEntities: builder.query<EntitySearchHit[], { id: string; q: string }>({
      query: ({ id, q }) => ({
        url: `/norag/expedientes/${id}/entities`,
        params: { q },
      }),
      transformResponse: (response: { data: EntitySearchHit[] }) => response.data,
    }),
    deleteExpediente: builder.mutation<{ deleted: boolean }, string>({
      query: id => ({
        url: `/norag/expedientes/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { data: { deleted: boolean } }) => response.data,
      invalidatesTags: [{ type: 'NoragExpediente', id: 'LIST' }],
    }),
    getCopilotStatus: builder.query<{ configured: boolean }, void>({
      query: () => '/norag/copilot/status',
      transformResponse: (response: { data: { configured: boolean } }) => response.data,
    }),
    listCopilotConversations: builder.query<CopilotConversationSummary[], void>({
      query: () => '/norag/copilot/conversations',
      transformResponse: (response: { data: CopilotConversationSummary[] }) => response.data,
      providesTags: [{ type: 'NoragCopilot', id: 'LIST' }],
    }),
    createCopilotConversation: builder.mutation<CopilotConversationSummary, { title?: string } | void>({
      query: body => ({
        url: '/norag/copilot/conversations',
        method: 'POST',
        body: body ?? {},
      }),
      transformResponse: (response: { data: CopilotConversationSummary }) => response.data,
      invalidatesTags: [{ type: 'NoragCopilot', id: 'LIST' }],
    }),
    getCopilotConversation: builder.query<CopilotConversationDetail, string>({
      query: id => `/norag/copilot/conversations/${id}`,
      transformResponse: (response: { data: CopilotConversationDetail }) => response.data,
      providesTags: (_r, _e, id) => [{ type: 'NoragCopilot', id }],
    }),
    renameCopilotConversation: builder.mutation<
      { id: string; title: string; updatedAt: string },
      { id: string; title: string }
    >({
      query: ({ id, title }) => ({
        url: `/norag/copilot/conversations/${id}`,
        method: 'PATCH',
        body: { title },
      }),
      transformResponse: (response: { data: { id: string; title: string; updatedAt: string } }) =>
        response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'NoragCopilot', id: 'LIST' },
        { type: 'NoragCopilot', id },
      ],
    }),
    deleteCopilotConversation: builder.mutation<void, string>({
      query: id => ({
        url: `/norag/copilot/conversations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'NoragCopilot', id: 'LIST' }],
    }),
    sendCopilotMessage: builder.mutation<
      { userMessage: CopilotMessage; assistantMessage: CopilotMessage },
      { id: string; content: string }
    >({
      query: ({ id, content }) => ({
        url: `/norag/copilot/conversations/${id}/messages`,
        method: 'POST',
        body: { content },
      }),
      transformResponse: (response: {
        data: { userMessage: CopilotMessage; assistantMessage: CopilotMessage };
      }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'NoragCopilot', id },
        { type: 'NoragCopilot', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListDomainsQuery,
  useGetDomainQuery,
  useGetDomainContentQuery,
  useIngestFileMutation,
  useIngestUrlMutation,
  useDeprecateDomainMutation,
  useReactivateDomainMutation,
  useLazyGetDomainDeleteImpactQuery,
  useDeleteDomainPermanentMutation,
  useListTemplatesQuery,
  useUploadTemplateMutation,
  useGetNoragStatsQuery,
  useRunNoragLintMutation,
  useListJobsQuery,
  useListFoldersQuery,
  useCreateFolderMutation,
  useRebuildFolderIndexMutation,
  useListBooksQuery,
  useGetBookQuery,
  useIngestBookMutation,
  usePatchBookPlanMutation,
  useConfirmBookMutation,
  useLazyGetBookDeleteImpactQuery,
  useDeleteBookMutation,
  useListBotsQuery,
  useGetBotQuery,
  useCreateBotMutation,
  useUpdateBotMutation,
  useDeactivateBotMutation,
  useRotateBotTokenMutation,
  useExportOkfBundleMutation,
  useListExpedienteIndexesQuery,
  useBrowseHierarchyChildrenQuery,
  useLazyBrowseHierarchyChildrenQuery,
  useLocateHierarchyDataQuery,
  useLazyLocateHierarchyDataQuery,
  useListExpedienteIngestionsQuery,
  useGetExpedienteIngestionQuery,
  useIngestExpedienteMutation,
  useGetExpedienteReportQuery,
  useDownloadExpedienteReportPdfMutation,
  useListExpedienteDocumentsQuery,
  useListExpedienteDocAnalysesQuery,
  useAddExpedienteDocumentsMutation,
  useReplaceExpedienteDocumentMutation,
  useDeleteExpedienteDocumentMutation,
  useRegenerateExpedienteReportMutation,
  useGetExpedienteTimelineQuery,
  useSearchExpedienteEntitiesQuery,
  useLazySearchExpedienteEntitiesQuery,
  useDeleteExpedienteMutation,
  useGetCopilotStatusQuery,
  useListCopilotConversationsQuery,
  useCreateCopilotConversationMutation,
  useGetCopilotConversationQuery,
  useRenameCopilotConversationMutation,
  useDeleteCopilotConversationMutation,
  useSendCopilotMessageMutation,
} = noragApiSlice;
